import React from 'react';
import { SlaDeltaStripCell, SlaDeltaStripLeg, SlaVerdict } from '../../../interfaces/panel-data';
import { formatDateFromDate } from '../../../utils/date-helper';
import { formatDuration } from '../../../utils/format-duration';
import { VERDICT_LABELS } from './sla-verdict-labels';
import './DeltaStrip.scss';

export type DeltaStripCell = SlaDeltaStripCell;

/** Just enough of the tier's group row to say that a leg is expected and what to call it. */
export interface DeltaStripLegName {
  key: string;
  displayName: string;
}

/**
 * Eight verdicts onto six colours. Both overruns share the red, and STALE and UNMEASURED share the grey
 * because neither is a claim about compliance.
 *
 * NOT_REGISTERED no longer joins them. On a delta leg it now states a fact rather than declining to judge
 * one — the leg was due and never started — and the grey it used to borrow is the panel's colour for
 * *un-judged*, which is the opposite of what a workflow that never ran is. It gets a colour of its own
 * here and only here: the band bars still use the grey, where the same verdict really does mean "no budget
 * applies to these rows".
 *
 * FAILED borrows neither. It is not an overrun — nothing was compared against a budget — but neither is it
 * un-judged for want of a rule, so either colour would re-merge the states it exists to separate.
 */
const CELL_MODIFIERS: Record<SlaVerdict, string> = {
  WITHIN_SLA: 'within',
  AT_RISK: 'at-risk',
  BREACHING: 'breaching',
  BREACHED: 'breaching',
  FAILED: 'failed',
  NOT_REGISTERED: 'missing',
  STALE: 'not-registered',
  UNMEASURED: 'not-registered',
};

interface DeltaStripProps {
  cells: DeltaStripCell[];
  /**
   * The legs the delta tier lists, in the order it lists them. Used to name a leg a cell has no entry for
   * at all, which is the difference between *not started yet* and *never started*: without it the tooltip
   * would have to read a leg's absence as a leg's failure.
   */
  legNames: DeltaStripLegName[];
  /** Rendered only when the cap withheld cells — see `isCapped`. */
  capNote: string | undefined;
  /** How many cells the backend was willing to send, which is what makes truncation detectable. */
  cap: number;
  clockExplanation: string | undefined;
}

const plural = (count: number, noun: string): string =>
  `${count.toLocaleString()} ${noun}${count === 1 ? '' : 's'}`;

/**
 * One leg's line of the tooltip. A leg that never started is named and marked rather than merely coloured,
 * because the cell's colour can only carry the worst of the two legs and the reader needs to know which.
 */
const legLine = (leg: SlaDeltaStripLeg): string => {
  if (!leg.triggered) {
    const overdue = leg.elapsedMs === null ? '' : `, overdue ${formatDuration(leg.elapsedMs)}`;
    return `✗ ${leg.displayName}: never triggered — no workflows at all${overdue}`;
  }
  const ran = leg.elapsedMs === null ? 'no runtime recorded' : `ran ${formatDuration(leg.elapsedMs)}`;
  // Not "of no budget configured": the budget exists and the panel declined to compare against it, which is
  // the opposite claim. The runtime is still shown — a leg can burn nineteen hours before erroring.
  const spent =
    leg.verdict === 'FAILED'
      ? `${ran}, not judged against its budget`
      : `${ran} of ${
          leg.budgetMs === null ? 'no budget configured' : `budget ${formatDuration(leg.budgetMs)}`
        }`;
  // The ids are the point of the tooltip — they are what an operator takes to the workflow console — so
  // they are listed rather than counted. A count with no ids still beats neither.
  const workflows =
    leg.workflowIds === null
      ? plural(leg.workflowCount ?? 0, 'workflow')
      : `${plural(leg.workflowCount ?? 0, 'workflow')}: ${leg.workflowIds}`;
  return `${leg.displayName}: ${leg.state ?? 'state unknown'} · ${spent} · ${workflows} · ${
    VERDICT_LABELS[leg.verdict]
  }`;
};

/**
 * Native `title` rather than a popover: there is one per delta, up to the cap of them on screen, and a
 * popover per cell would be several hundred listeners for a hover.
 *
 * Every leg the tier knows about gets a line whether the delta has one or not, so that a cell answers
 * "did SCI and DTP run?" on its own terms rather than by omission. A leg the tier does not list is still
 * described, at the end — the strip is the only place a delta's legs are enumerated, and dropping one
 * would read as "not due yet".
 */
const cellTitle = (cell: DeltaStripCell, legNames: DeltaStripLegName[]): string => {
  const found = new Map(cell.legs.map((leg) => [leg.leg, leg]));
  const named = legNames.map((expected) => {
    const leg = found.get(expected.key);
    return leg === undefined ? `${expected.displayName}: not due yet` : legLine(leg);
  });
  const unexpected = cell.legs
    .filter((leg) => !legNames.some((expected) => expected.key === leg.leg))
    .map(legLine);
  return [
    `${cell.id} · created ${formatDateFromDate(new Date(cell.createdAtMs))} · ${
      VERDICT_LABELS[cell.verdict]
    }`,
    ...named,
    ...unexpected,
  ].join('\n');
};

/**
 * One website's EPC deltas, one cell each, in creation order — the shape of the chain over time.
 *
 * The cells are sorted here because the aggregation returns them newest first, so that the cap drops the
 * oldest deltas rather than the newest. Drawn in the payload's order the strip would tell the story
 * backwards.
 *
 * Nothing is rendered without cells: an empty bordered box asserts that a website's chain is empty, which
 * is indistinguishable from one nobody has indexed yet.
 */
export const DeltaStrip: React.FC<DeltaStripProps> = ({
  cells,
  legNames,
  capNote,
  cap,
  clockExplanation,
}) => {
  if (cells.length === 0) {
    return null;
  }

  const ordered = [...cells].sort((left, right) => left.createdAtMs - right.createdAtMs);
  const oldest = ordered[0];
  const newest = ordered[ordered.length - 1];
  // The cap limits what was sent, and there is no pre-cap total to compare against, so a full strip is
  // treated as a partial one. Proving truncation before saying so would mean never saying so.
  const isCapped = cells.length >= cap;
  // Counted from the legs rather than from the cell verdicts, because one cell can be missing both legs and
  // a reader chasing the colour is looking for workflows to start, not for squares.
  const untriggered = ordered.flatMap((cell) => cell.legs.filter((leg) => !leg.triggered));
  // Counted from the legs for the same reason, and counted at all because a failed leg appears nowhere else:
  // it is off the band bars by design and is not an exception row. Without this, a website whose every leg
  // errored renders a strip of colour and no number.
  const failed = ordered.flatMap((cell) => cell.legs.filter((leg) => leg.verdict === 'FAILED'));

  return (
    <div className="ovp-dstrip">
      {clockExplanation && <p className="ovp-dstrip__note">{clockExplanation}</p>}

      <div
        className="ovp-dstrip__cells"
        role="img"
        aria-label={`${ordered.length.toLocaleString()} deltas in creation order, coloured by SLA verdict`}
        data-testid="sla-delta-strip"
      >
        {ordered.map((cell) => (
          <span
            key={cell.id}
            className={`ovp-dstrip__cell ovp-dstrip__cell--${CELL_MODIFIERS[cell.verdict]}`}
            title={cellTitle(cell, legNames)}
            data-testid={`delta-cell-${CELL_MODIFIERS[cell.verdict]}`}
          />
        ))}
      </div>

      {/* The fifth colour, explained where it is used and only while it is on screen: the panel-wide key
          carries the four band colours, and a key for a colour no cell is drawn in is noise. */}
      {untriggered.length > 0 && (
        <p className="ovp-dstrip__note" data-testid="sla-delta-strip-missing-note">
          <span className="ovp-dstrip__swatch" aria-hidden="true" />
          {`Never triggered: ${plural(untriggered.length, 'leg')} due with no workflows at all.` +
            ' Hover a cell for which delta and which leg.'}
        </p>
      )}

      {/* The sixth, on the same terms. It says "not counted against" outright, because a failure absent from
          the bars reads as an omission unless the panel admits to it. */}
      {failed.length > 0 && (
        <p className="ovp-dstrip__note" data-testid="sla-delta-strip-failed-note">
          <span className="ovp-dstrip__swatch ovp-dstrip__swatch--failed" aria-hidden="true" />
          {`Failed: ${plural(failed.length, 'leg')} errored, not counted against any budget.` +
            ' Hover a cell for which delta and which leg.'}
        </p>
      )}

      {/* A strip of identical cells has no other way to say what span it covers, and the last 500 deltas
          are a very different window on a busy website than on a quiet one. */}
      <div className="ovp-dstrip__axis">
        <span>{`${oldest.id} · ${formatDateFromDate(new Date(oldest.createdAtMs))}`}</span>
        <span className="ovp-dstrip__axis-span">
          {`${ordered.length.toLocaleString()} deltas over ${formatDuration(
            Math.max(0, newest.createdAtMs - oldest.createdAtMs)
          )}`}
        </span>
        <span>{`${newest.id} · ${formatDateFromDate(new Date(newest.createdAtMs))}`}</span>
      </div>

      {isCapped && capNote && (
        <p className="ovp-dstrip__note" data-testid="sla-delta-strip-cap-note">
          {capNote}
        </p>
      )}
    </div>
  );
};
